import { analytics } from '~/_plugins/analytics/core'
import { useAccountStore } from '~/stores/account'
import { useFantasySeasonStore } from '~/stores/fantasy/season'
import type {
  FantasyCardGrades,
  FantasyCardTypeId,
  FantasyDayStatus,
  FantasyGradeFilterValue,
  FantasyLeaderboardType,
  FantasyPurchaseCategory,
  FantasyReward,
  FantasyScreenName
} from '~/types/fantasy'
import { FANTASY_SCREENS } from './constants'

export const useFantasyAnalytics = () => {
  const accountStore = useAccountStore()
  const fantasySeasonStore = useFantasySeasonStore()

  const tournamentId = computed(() => fantasySeasonStore.data?.id ?? null)
  const tournamentStatus = computed(() => fantasySeasonStore.data?.status ?? '')

  const basePayload = (screenName?: FantasyScreenName) => ({
    tournament_id: tournamentId.value,
    tournament_status: tournamentStatus.value,
    authorization_flag: accountStore.isAuthorized,
    ...(screenName ? { screen_name: screenName } : {})
  })

  const trackFantasyScreenView = () => {
    analytics('LigaPass_Fantasy_ScreenView', {
      authorization_flag: accountStore.isAuthorized,
      screen_name: FANTASY_SCREENS.all
    })
  }

  const trackTournamentButtonClick = (payload: {
    tournamentId: number
    tournamentStatus: string
  }) => {
    analytics('LigaPass_FantasyTournament_ButtonClick', {
      tournament_id: payload.tournamentId,
      tournament_status: payload.tournamentStatus,
      screen_name: FANTASY_SCREENS.all
    })
  }

  const trackTournamentScreenView = () => {
    analytics('LigaPass_FantasyTournament_ScreenView', basePayload())
  }

  const trackShopScreenView = (category: FantasyPurchaseCategory = 'card') => {
    analytics('LigaPass_FantasyShop_ScreenView', {
      ...basePayload(FANTASY_SCREENS.shop),
      category
    })
  }

  const trackMyCardsScreenView = (
    category: FantasyPurchaseCategory = 'card'
  ) => {
    analytics('LigaPass_FantasyMyCards_ScreenView', {
      ...basePayload(FANTASY_SCREENS.collection),
      category
    })
  }

  const trackGameDayScreenView = (payload: {
    dayDate: string
    dayStatus: FantasyDayStatus
  }) => {
    analytics('LigaPass_FantasyGameDay_ScreenView', {
      ...basePayload(FANTASY_SCREENS.gameDay),
      day_date: payload.dayDate,
      day_status: payload.dayStatus
    })
  }

  const trackLeaderboardScreenView = (payload: {
    leaderboardType: FantasyLeaderboardType
    screenName: FantasyScreenName
  }) => {
    analytics('LigaPass_FantasyLeaderboard_ScreenView', {
      ...basePayload(payload.screenName),
      leaderboard_type: payload.leaderboardType
    })
  }

  const trackLeaderboardClick = (payload: {
    leaderboardType: FantasyLeaderboardType
    screenName: FantasyScreenName
  }) => {
    analytics('LigaPass_FantasyLeaderboard_ButtonClick', {
      ...basePayload(payload.screenName),
      leaderboard_type: payload.leaderboardType
    })
  }

  const trackShopClick = (
    screenName: FantasyScreenName = FANTASY_SCREENS.main
  ) => {
    analytics('LigaPass_FantasyShop_ButtonClick', basePayload(screenName))
  }

  const trackMyCardsClick = (
    screenName: FantasyScreenName = FANTASY_SCREENS.main
  ) => {
    analytics('LigaPass_FantasyMyCards_ButtonClick', basePayload(screenName))
  }

  const trackStarterSetClick = () => {
    analytics('LigaPass_FantasyStarterSet_ButtonClick', {
      ...basePayload(FANTASY_SCREENS.main)
    })
  }

  const trackGameDayClick = (payload: {
    dayDate: string
    dayStatus: FantasyDayStatus
    screenName?: FantasyScreenName
  }) => {
    analytics('LigaPass_FantasyGameDay_ButtonClick', {
      ...basePayload(payload.screenName ?? FANTASY_SCREENS.main),
      day_date: payload.dayDate,
      day_status: payload.dayStatus
    })
  }

  const trackRewardClick = (payload: {
    dayDate: string
    dayStatus: FantasyDayStatus
    reward: FantasyReward
    screenName: FantasyScreenName
  }) => {
    analytics('LigaPass_FantasyReward_ButtonClick', {
      ...basePayload(payload.screenName),
      day_date: payload.dayDate,
      day_status: payload.dayStatus,
      reward_type: payload.reward.resource?.name ?? 'unknown',
      reward_tier: `top_${payload.reward.top_percent}`
    })
  }

  const trackPickClick = (payload: {
    dayDate: string
    pickType: FantasyCardTypeId
    screenName: FantasyScreenName
    itemId?: number
    grade?: FantasyCardGrades
  }) => {
    analytics('LigaPass_FantasyPick_ButtonClick', {
      ...basePayload(payload.screenName),
      day_date: payload.dayDate,
      pick_type: payload.pickType,
      ...(payload.itemId != null ? { item_id: payload.itemId } : {}),
      ...(payload.grade ? { grade: payload.grade } : {})
    })
  }

  const trackFilterClick = (payload: {
    grade: FantasyGradeFilterValue
    screenName: FantasyScreenName
    category?: FantasyPurchaseCategory
  }) => {
    analytics('LigaPass_FantasyFilter_ButtonClick', {
      ...basePayload(payload.screenName),
      category: payload.category ?? 'card',
      grade: payload.grade
    })
  }

  const trackPurchaseClick = (payload: {
    category: FantasyPurchaseCategory
    itemId: number
    grade: FantasyGradeFilterValue | FantasyCardGrades
    screenName: FantasyScreenName
  }) => {
    analytics('LigaPass_FantasyPurchase_ButtonClick', {
      ...basePayload(payload.screenName),
      category: payload.category,
      item_id: payload.itemId,
      grade: payload.grade
    })
  }

  const trackSellClick = (payload: {
    category: FantasyPurchaseCategory
    itemId: number
    grade: FantasyGradeFilterValue | FantasyCardGrades
    screenName: FantasyScreenName
  }) => {
    analytics('LigaPass_FantasySell_ButtonClick', {
      ...basePayload(payload.screenName),
      category: payload.category,
      item_id: payload.itemId,
      grade: payload.grade
    })
  }

  const trackUpgradeClick = (payload: {
    category: FantasyPurchaseCategory
    itemId: number
    grade: FantasyGradeFilterValue | FantasyCardGrades
    nextGrade: FantasyCardGrades
    screenName: FantasyScreenName
  }) => {
    analytics('LigaPass_FantasyUpgrade_ButtonClick', {
      ...basePayload(payload.screenName),
      category: payload.category,
      item_id: payload.itemId,
      grade: payload.grade,
      next_grade: payload.nextGrade
    })
  }

  const trackBackClick = (payload: {
    sourceScreenName: FantasyScreenName
    targetScreenName?: FantasyScreenName | string | null
  }) => {
    let target = payload.targetScreenName

    if (!target) {
      target =
        payload.targetScreenName === undefined ? FANTASY_SCREENS.main : null
    }

    analytics('LigaPass_FantasyBack_ButtonClick', {
      ...basePayload(payload.sourceScreenName),
      source_screen_name: payload.sourceScreenName,
      target_screen_name: target
    })
  }

  return {
    trackFantasyScreenView,
    trackTournamentButtonClick,
    trackTournamentScreenView,
    trackShopScreenView,
    trackMyCardsScreenView,
    trackGameDayScreenView,
    trackLeaderboardScreenView,
    trackLeaderboardClick,
    trackShopClick,
    trackMyCardsClick,
    trackStarterSetClick,
    trackGameDayClick,
    trackRewardClick,
    trackPickClick,
    trackFilterClick,
    trackPurchaseClick,
    trackSellClick,
    trackUpgradeClick,
    trackBackClick
  }
}
